import api from ".";
import { getToken } from "../Auth/Token";

const ORDER_COLLECTION_KEYS = ["orders", "data", "results", "items", "docs"];
const DEFAULT_ORDER_PAGE_SIZE = 500;

const getPaginationValue = (payload, keys) => {
  for (const key of keys) {
    if (payload?.[key] != null) {
      return payload[key];
    }

    if (payload?.pagination?.[key] != null) {
      return payload.pagination[key];
    }
  }

  return null;
};

const getWalmartOrderLines = (source) => {
  const lines =
    source?.OrderItems ||
    source?.orderItems ||
    source?.orderLines?.orderLine ||
    source?.orderLines ||
    source?.lines ||
    [];

  return Array.isArray(lines) ? lines : [lines];
};

const getLineCharges = (line) => {
  const charges = line?.charges?.charge || line?.charges || [];

  return Array.isArray(charges) ? charges : [charges];
};

const getChargeAmount = (charge) =>
  Number(
    charge?.chargeAmount?.amount ||
      charge?.chargeAmount?.Amount ||
      charge?.amount ||
      0,
  );

const getLineItemPrice = (line) => {
  const charges = getLineCharges(line);
  const afterDiscountCharge = charges.find(
    (charge) =>
      String(charge?.chargeName || "").toUpperCase() ===
      "ITEM_PRICE_AFTER_DISCOUNT",
  );

  if (afterDiscountCharge) {
    return getChargeAmount(afterDiscountCharge);
  }

  const itemPriceCharge = charges.find(
    (charge) =>
      String(charge?.chargeName || "").toUpperCase() === "ITEMPRICE" ||
      String(charge?.chargeType || "").toUpperCase() === "PRODUCT",
  );

  return itemPriceCharge ? getChargeAmount(itemPriceCharge) : 0;
};

const getOrderItemsTotal = (source) => {
  const total = getWalmartOrderLines(source).reduce(
    (sum, line) => sum + getLineItemPrice(line),
    0,
  );

  return total > 0 ? total : null;
};

const getLineQuantity = (line) =>
  Number(
    line?.QuantityOrdered ||
      line?.quantityOrdered ||
      line?.quantity ||
      line?.qty ||
      line?.orderLineQuantity?.amount ||
      1,
  );

const getLineSku = (line) =>
  line?.SellerSKU ||
  line?.sellerSku ||
  line?.sellerSkus ||
  line?.sku ||
  line?.SKU ||
  line?.item?.sku ||
  "N/A";

const getLineCurrency = (line) =>
  getLineCharges(line).find((charge) => charge?.chargeAmount?.currency)
    ?.chargeAmount?.currency || null;

const getMarketplaceId = (source) => {
  const marketplaceId =
    source?.MarketplaceId ||
    source?.marketplaceId ||
    source?.marketplaceName ||
    source?.market;

  if (marketplaceId) {
    return marketplaceId;
  }

  const country = String(
    source?.shippingInfo?.postalAddress?.country ||
      source?.ShippingInfo?.PostalAddress?.Country ||
      "",
  ).toUpperCase();

  return country === "CA" || country === "CANADA" ? "Walmart.ca" : "Walmart US";
};

const normalizeOrder = (order, index) => {
  const source =
    order?.walmartOrderDetails || order?.details || order?.orderData || order || {};
  const orderItems = getWalmartOrderLines(source).map((line) => ({
    ...line,
    quantityOrdered: getLineQuantity(line),
    linePrice: getLineItemPrice(line),
    sellerSku: getLineSku(line),
    currencyCode: getLineCurrency(line),
  }));
  const orderItemsTotal = getOrderItemsTotal(source);
  const fallbackOrderTotal =
    source?.OrderTotal?.Amount ||
    source?.orderTotal?.amount ||
    source?.orderTotalAmount ||
    source?.OrderAmount ||
    source?.orderAmount ||
    source?.totalAmount ||
    null;
  const customerOrderId =
    source?.customerOrderId ||
    source?.CustomerOrderId ||
    order?.customerOrderId ||
    "N/A";
  const purchaseOrderId =
    source?.purchaseOrderId ||
    source?.PurchaseOrderId ||
    order?.purchaseOrderId ||
    "N/A";
  const walmartOrderId =
    source?.WalmartOrderId ||
    source?.walmartOrderId ||
    source?.purchaseOrderId ||
    source?.PurchaseOrderId ||
    source?.customerOrderId ||
    source?.CustomerOrderId ||
    source?.orderId ||
    order?.walmartOrderId ||
    order?.purchaseOrderId ||
    order?.customerOrderId ||
    order?.orderId ||
    "N/A";
  const salesOrderNumber =
    source?.salesOrderNumber ||
    source?.SalesOrderNumber ||
    source?.existingSalesOrderNumber ||
    order?.salesOrderNumber ||
    order?.existingSalesOrderNumber ||
    null;
  const salesOrderId =
    source?.salesOrderId ||
    source?.SalesOrderId ||
    source?.existingSalesOrderId ||
    order?.salesOrderId ||
    order?.existingSalesOrderId ||
    null;

  return {
    id:
      order?._id ||
      source?._id ||
      walmartOrderId ||
      `walmart-order-${index}`,
    walmartOrderId,
    customerOrderId,
    purchaseOrderId,
    marketplaceId: getMarketplaceId(source),
    orderStatus: source?.OrderStatus || source?.orderStatus || "Unknown",
    automationStatus:
      source?.automationStatus || order?.automationStatus || "N/A",
    queuedAt: source?.queuedAt || order?.queuedAt || null,
    processingStartedAt:
      source?.processingStartedAt || order?.processingStartedAt || null,
    processedAt: source?.processedAt || order?.processedAt || null,
    queueJobId: source?.queueJobId || order?.queueJobId || "N/A",
    purchaseDate:
      source?.PurchaseDate ||
      source?.purchaseDate ||
      source?.orderDate ||
      source?.createdAt ||
      null,
    lastUpdateDate:
      source?.LastUpdateDate ||
      source?.lastUpdateDate ||
      source?.updatedAt ||
      null,
    fulfillmentChannel:
      source?.FulfillmentChannel || source?.fulfillmentChannel || "N/A",
    salesChannel: source?.SalesChannel || source?.salesChannel || "N/A",
    orderTotal: orderItemsTotal ?? fallbackOrderTotal,
    currencyCode:
      orderItems.find((item) => item.currencyCode)?.currencyCode ||
      source?.OrderTotal?.CurrencyCode ||
      source?.orderTotal?.currencyCode ||
      source?.orderTotalCurrencyCode ||
      source?.CurrencyCode ||
      source?.currencyCode ||
      "USD",
    salesOrderNumber: salesOrderNumber || "N/A",
    salesOrderId: salesOrderId || "N/A",
    useExistingSalesOrder:
      source?.useExistingSalesOrder ??
      source?.shouldUseExistingSalesOrder ??
      order?.useExistingSalesOrder ??
      order?.shouldUseExistingSalesOrder ??
      null,
    automationAction:
      source?.automationAction ||
      source?.automationRoute ||
      source?.salesOrderAction ||
      order?.automationAction ||
      order?.automationRoute ||
      order?.salesOrderAction ||
      null,
    sellerSkus:
      source?.sellerSkus ||
      source?.SellerSKU ||
      source?.sellerSku ||
      source?.sku ||
      source?.SKU ||
      order?.sellerSkus ||
      "N/A",
    buyerName: source?.BuyerInfo?.BuyerName || source?.buyerName || "N/A",
    shipServiceLevel:
      source?.ShipServiceLevel || source?.shipServiceLevel || "N/A",
    orderItems:
      orderItems,
    raw: order,
  };
};

const extractOrders = (payload) => {
  if (Array.isArray(payload)) return payload;

  for (const key of ORDER_COLLECTION_KEYS) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const getPaginationState = (payload, orderCount) => {
  const totalPages = Number(
    getPaginationValue(payload, ["totalPages", "pages", "pageCount", "lastPage"]),
  );
  const currentPage = Number(
    getPaginationValue(payload, ["page", "currentPage", "pageNumber"]),
  );
  const nextPage = Number(getPaginationValue(payload, ["nextPage"]));
  const totalCount = Number(
    getPaginationValue(payload, [
      "total",
      "totalCount",
      "count",
      "totalDocs",
      "totalResults",
    ]),
  );
  const hasNextPageValue = getPaginationValue(payload, ["hasNextPage"]);

  return {
    totalPages: Number.isFinite(totalPages) ? totalPages : null,
    currentPage: Number.isFinite(currentPage) ? currentPage : null,
    nextPage: Number.isFinite(nextPage) ? nextPage : null,
    totalCount: Number.isFinite(totalCount) ? totalCount : null,
    hasNextPage:
      typeof hasNextPageValue === "boolean"
        ? hasNextPageValue
        : Number.isFinite(nextPage)
          ? nextPage > 0
          : Number.isFinite(totalPages) && Number.isFinite(currentPage)
            ? currentPage < totalPages
            : Number.isFinite(totalCount)
              ? orderCount < totalCount
              : false,
  };
};

const fetchStoredWalmartOrdersPage = async (token, page, limit) => {
  return api.get("/walmart/orders/stored", {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    params: {
      page,
      limit,
    },
  });
};

export const getStoredWalmartOrders = async () => {
  const token = getToken();
  const firstResponse = await fetchStoredWalmartOrdersPage(
    token,
    1,
    DEFAULT_ORDER_PAGE_SIZE,
  );
  const firstPayload = firstResponse.data;
  const firstBatch = extractOrders(firstPayload);
  const paginationState = getPaginationState(firstPayload, firstBatch.length);

  let combinedOrders = [...firstBatch];

  if (paginationState.hasNextPage) {
    const seenIds = new Set(
      firstBatch.map((order, index) => normalizeOrder(order, index).id),
    );
    let nextPage = paginationState.nextPage || 2;
    const finalPage = paginationState.totalPages;

    while (
      nextPage &&
      (!finalPage || nextPage <= finalPage)
    ) {
      const response = await fetchStoredWalmartOrdersPage(
        token,
        nextPage,
        DEFAULT_ORDER_PAGE_SIZE,
      );
      const payload = response.data;
      const batch = extractOrders(payload);

      if (!batch.length) {
        break;
      }

      batch.forEach((order, index) => {
        const normalized = normalizeOrder(order, combinedOrders.length + index);
        if (!seenIds.has(normalized.id)) {
          seenIds.add(normalized.id);
          combinedOrders.push(order);
        }
      });

      const state = getPaginationState(payload, combinedOrders.length);
      if (!state.hasNextPage) {
        break;
      }

      nextPage = state.nextPage || nextPage + 1;
    }
  }

  const orders = combinedOrders.map(normalizeOrder);

  return {
    ...firstPayload,
    orders,
  };
};

export const addWarehouseAndFulfillWalmartOrder = async (payload) => {
  const token = getToken();
  const requestPayload = {
    ...payload,
    priority: payload?.priority || "highest",
    requestedFrom: payload?.requestedFrom || "ui",
  };
  const response = await api.post(
    "/api/erpag/salesorder/addwarehouse-and-fulfill",
    requestPayload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  return response.data;
};

